<template>
  <div class="hotelux-search-bar" :class="{ sticky: isSticky }">
    <div class="search-bar-container">
      <div class="search-fields">
        <div class="search-field">
          <label class="field-label">Destination</label>
          <hotelux-location
            :placeholder="t('shared.common.searchDestinations')"
            :model-value="hotelSearchModel.location"
            :location-disabled="locationDisabled"
            @update:model-value="handleLocationChange"
          />
        </div>

        <div class="search-field">
          <label class="field-label">Check in - Check out</label>
          <date-range-calendar
            :is-sticky="isSticky"
            :single-month="isSticky"
            v-model="searchModel.stay.date"
            @change="handleDateChange"
          />
        </div>

        <div class="search-field">
          <label class="field-label">Rooms & Guests</label>
          <hotelux-travelers
            :is-sticky="isSticky"
            v-model="searchModel.stay.guest"
            @change="handleGuestChange"
          >
            <template #icon>
              <font-icon
                class="icon-hotelux calendar-icon"
                icon="icon-hotelux-Adults"
              />
            </template>
          </hotelux-travelers>
        </div>
      </div>

      <button class="search-button" @click="handleSearch">
        <font-icon
          icon="icon-hotelux-Search"
          class="icon-hotelux search-icon"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import _ from "lodash";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";

import DateRangeCalendar from "../DateRangeCalendar/index.vue";
import HoteluxLocation from "../Location/index.vue";
import HoteluxTravelers from "../Travelers/index.vue";

const props = defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  locationDisabled: {
    type: Boolean,
    default: false
  },
  locationRefIgnored: {
    type: Boolean,
    default: false
  },
  isSticky: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["search-change", "search-click"]);

const { t } = useI18n();
const hotelStore = useHotelStoreHook();
const { searchModel, hotelSearchModel } = storeToRefs(hotelStore);

const performSearch = () => {
  const locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (locationRef || props.locationRefIgnored) {
    emit("search-change");
  }
};

const debouncedSearch = _.debounce(performSearch, 300);

const instantSearch = () => {
  debouncedSearch.cancel();
  const locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (!locationRef && !props.locationRefIgnored) {
    ElMessage.error(t("shared.hotel.search.locationRequired"));
    return;
  }
  performSearch();
};
// const getDateFormat = () => {
//   const currentLang = locale.value;

//   // 判断语言组
//   const CJK_LANGUAGES = ["zh-TW", "zh-HK", "ko", "jp", "ja"];
//   const TH_LANGUAGE = "th";
//   const isCJK = CJK_LANGUAGES.includes(currentLang);
//   const isTH = currentLang === TH_LANGUAGE;

//   let format = "";
//   if (isCJK) {
//     format = "MM/DD ddd";
//   } else if (isTH) {
//     format = "DD MMM ddd";
//   } else {
//     format = "ddd, MMM DD";
//   }

//   return format;
// };
const handleLocationChange = newLocation => {
  hotelStore.hotelSearchModel.location = newLocation;

  if (newLocation) {
    hotelStore.hotelSearchModel.locationRef = newLocation._id;
    hotelStore.hotelSearchModel.locationType = newLocation.locationType;
  } else {
    hotelStore.hotelSearchModel.locationRef = "";
    hotelStore.hotelSearchModel.locationType = "";
    hotelStore.hotelSearchModel.filter = {};
    debouncedSearch();
    return;
  }

  const { locationType, _id, showLabel } = newLocation;
  let newFilter = {};

  switch (locationType) {
    case "city":
      newFilter = { city: { _id } };
      break;
    case "moduleTpHotel":
      newFilter = { hotels: [{ _id }] };
      hotelStore.selectedHotel = newLocation;
      break;
    case "googleMapPolitical":
      newFilter = {
        _biz: { googleMapPolitical: { place_id: _id, description: showLabel } }
      };
      break;
    case "search":
      newFilter = { search: showLabel };
      break;
  }
  hotelStore.hotelSearchModel.filter = newFilter;

  if (locationType === "moduleTpHotel") {
    instantSearch();
  } else {
    debouncedSearch();
  }
};

const handleDateChange = date => {
  hotelStore.searchModel.stay.date = {
    checkIn: dayjs(date[0]).format("YYYY-MM-DD"),
    checkOut: dayjs(date[1]).format("YYYY-MM-DD")
  };
  debouncedSearch();
};

const handleGuestChange = guest => {
  hotelStore.searchModel.stay.guest = guest;
  debouncedSearch();
};

const handleSearch = () => {
  const locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (!locationRef && !props.locationRefIgnored) {
    ElMessage.error(t("shared.hotel.search.locationRequired"));
    return;
  }
  emit("search-click");
};

defineOptions({
  name: "HoteluxSearchBar"
});
</script>

<style lang="scss" scoped>
.hotelux-search-bar {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 max(60px, calc((100% - 1520px) / 2));

  // 地名占位文案：设计稿用灰色，字重沿用组件自身的 lato-bold
  :deep(.hotelux-location) {
    // 图标与上方 label 左对齐
    .el-input__prefix {
      margin-left: 0;
    }

    .el-input__wrapper {
      width: 100%;
      // 右侧留出间距，清空按钮不再紧贴分隔线
      padding-right: 12px;
      overflow: hidden;
    }

    .el-input__inner {
      display: block;
      width: 100%;
      overflow: hidden;
      box-sizing: border-box;
      text-overflow: ellipsis;
      white-space: nowrap;

      &::placeholder {
        color: #757575;
      }
    }
  }

  &.sticky {
    // 吸顶态左右各留 10px 外边距，窄宽度下也不会贴住 logo / 右侧币种
    padding: 0 10px;

    // 吸顶态收窄到 528 × 56（外框 530 × 58），白描边 + 浅灰渐变底
    .search-bar-container {
      width: 528px;
      height: 56px;
      background: #fff;
      // background: linear-gradient(90deg, #f4f4f4 0%, #f1f1f1 100%);
      border: 1px solid #fff;
      box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
    }

    .search-field {
      height: 32px;
      padding-left: 12px;

      // 吸顶态三列宽度与页面内不同，按设计稿固定
      &:nth-child(1) {
        width: 212px;
      }

      &:nth-child(2) {
        width: 148px;
      }

      &:nth-child(3) {
        width: 112px;
      }
    }

    .field-label {
      display: none;
    }

    .search-button {
      width: 32px;
      height: 32px;
      margin: 12px;
    }

    .search-icon {
      font-size: 12px;
    }

    // 吸顶态地名收小到 12px，图标按设计稿同步收小
    :deep(.hotelux-location) {
      .el-input__prefix {
        width: 16px;
        height: 16px;
        margin: 0 8px 0 0;
      }

      .location-icon {
        font-size: 16px;
      }

      .el-input__inner {
        // 吸顶态设计稿为常规字重
        font-family: lato;
        font-size: 12px;

        &::placeholder {
          font-family: lato;
          font-size: 12px;
        }
      }

      // 日文吸顶态沿用组件内的 Noto Sans JP，并取消 Medium（吸顶态不加粗）
      &.is-jp {
        .el-input__inner {
          font-family: NotoSansJP-Variable, sans-serif;
          font-weight: 400;

          &::placeholder {
            font-family: NotoSansJP-Variable, sans-serif;
            font-weight: 400;
          }
        }
      }
    }

    // 吸顶态日期与间夜分两行，间夜为灰色小字
    :deep(.hotelux-date-range-calendar) {
      .calendar-trigger {
        display: grid;
        grid-template-columns: 16px auto;
        column-gap: 8px;
        align-items: center;
      }

      .calendar-icon {
        grid-row: 1 / 3;
        margin-right: 0;
        font-size: 16px;
      }

      .trigger-date {
        font-family: lato;
        font-size: 12px;
        line-height: 16px;
      }

      .nights-badge {
        grid-column: 2;
        height: auto;
        padding: 0;
        margin-left: 0;
        font-family: lato;
        font-size: 12px;
        line-height: 16px;
        color: #757575;
        background: none;
      }
    }

    // 吸顶态房间与成人分两行展示
    :deep(.hotelux-travelers) {
      .travelers-trigger {
        display: grid;
        grid-template-columns: 16px auto;
        column-gap: 8px;
        align-items: center;
      }

      .travelers-trigger > .icon-hotelux {
        grid-row: 1 / 3;
        margin-right: 0;
        font-size: 14px;
      }

      .trigger-text {
        font-family: lato;
        font-size: 12px;
        line-height: 16px;
        white-space: normal;
        // 两行等高，避免把 "2 Adult(s)" 拆开
        text-wrap: balance;
      }
    }
  }
}

.search-bar-container {
  position: relative;
  display: flex;
  align-items: stretch;
  // 内容区 960 × 90，1px 白描边绘制在内容区外，与设计稿外框一致
  box-sizing: content-box;
  width: 960px;
  height: 90px;
  background: #fff;
  // background: linear-gradient(90deg, #f4f4f4 0%, #f1f1f1 100%);
  // border-image: linear-gradient(91deg, rgba(255, 255, 255, 1)) 1 1;
  border: 1px solid #ffffff;
  box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.search-fields {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0;
}

.search-field {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 50px;
  min-width: 0;
  padding-left: 20px;

  // 三列宽度按设计稿固定，分隔线（列右侧 1px 边框）不随文案长短偏移
  &:nth-child(1) {
    width: 386px;
  }

  &:nth-child(2) {
    width: 272px;
  }

  &:nth-child(3) {
    width: 224px;
  }

  &:not(:last-child) {
    border-right: 1px solid #d2d3d4;
  }
}

.field-label {
  font-family: "lato-bold";
  font-size: 10px;
  color: #757575;
  line-height: 18px;
  letter-spacing: 0;
  margin-bottom: 8px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.field-divider {
  width: 1px;
  height: 50px;
  background: #e9e9e9;
  margin: 0;
}

.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 66px;
  height: 66px;
  margin: 12px 12px 12px 0;
  background: #2a2c35;
  border: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #000;
  }
}

.search-icon {
  font-size: 24px;
  color: #fff;
}

.calendar-icon {
  margin-right: 12px;
  font-size: 18px;
}
</style>
